"use client";
import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { cn } from "../../../lib/src/utils";

// Safe helpers
const setDisplayName = (comp: any, name: string) => {
  try { Object.defineProperty(comp, "displayName", { value: name, configurable: true }); } catch {}
};
const has = (obj: any, key: string) => obj && Object.prototype.hasOwnProperty.call(obj, key);

// Root
const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive ref={ref} className={cn("bg-background text-foreground", className)} {...props} />
));
setDisplayName(Command, "Command");

// Subcomponents (guard every primitive access)
const PrimitiveInput = has(CommandPrimitive as any, "Input")
  ? (CommandPrimitive as any).Input
  : ((props: any) => <input {...props} />);

const CommandInput = React.forwardRef<
  React.ElementRef<typeof PrimitiveInput>,
  React.ComponentPropsWithoutRef<typeof PrimitiveInput>
>(({ className, ...props }, ref) => (
  <PrimitiveInput ref={ref} className={cn("h-9 px-3 outline-none w-full", className)} {...props} />
));
setDisplayName(CommandInput, "CommandInput");

const PrimitiveList = has(CommandPrimitive as any, "List")
  ? (CommandPrimitive as any).List
  : ((props: any) => <div {...props} role="listbox" />);

const CommandList = React.forwardRef<
  React.ElementRef<typeof PrimitiveList>,
  React.ComponentPropsWithoutRef<typeof PrimitiveList>
>(({ className, ...props }, ref) => (
  <PrimitiveList ref={ref} className={cn("max-h-64 overflow-auto", className)} {...props} />
));
setDisplayName(CommandList, "CommandList");

const PrimitiveItem = has(CommandPrimitive as any, "Item")
  ? (CommandPrimitive as any).Item
  : ((props: any) => <div {...props} role="option" />);

const CommandItem = React.forwardRef<
  React.ElementRef<typeof PrimitiveItem>,
  React.ComponentPropsWithoutRef<typeof PrimitiveItem>
>(({ className, ...props }, ref) => (
  <PrimitiveItem ref={ref} className={cn("px-3 py-2 cursor-pointer", className)} {...props} />
));
setDisplayName(CommandItem, "CommandItem");

const PrimitiveGroup = has(CommandPrimitive as any, "Group")
  ? (CommandPrimitive as any).Group
  : ((props: any) => <div {...props} role="group" />);

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof PrimitiveGroup>,
  React.ComponentPropsWithoutRef<typeof PrimitiveGroup>
>(({ className, ...props }, ref) => (
  <PrimitiveGroup ref={ref} className={cn("mt-2", className)} {...props} />
));
setDisplayName(CommandGroup, "CommandGroup");

const PrimitiveSeparator = has(CommandPrimitive as any, "Separator")
  ? (CommandPrimitive as any).Separator
  : ((props: any) => <hr {...props} />);

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof PrimitiveSeparator>,
  React.ComponentPropsWithoutRef<typeof PrimitiveSeparator>
>(({ className, ...props }, ref) => (
  <PrimitiveSeparator ref={ref} className={cn("my-2 border-border", className)} {...props} />
));
setDisplayName(CommandSeparator, "CommandSeparator");

export {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandGroup,
  CommandSeparator,
};

// CommandBoundary is exported separately to avoid SSR issues